import React, { ChangeEvent, useState } from 'react'
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material'
import { exportCsv } from 'shared/utils'
import { Column } from './types'
import { ConfirmDialog } from 'shared/ui'

/**
 * Renders a button that exports table data to CSV with column selection.
 * @param title - Base filename for the exported CSV
 * @param buttonLabel - Button label text
 * @param columnInfo - Column configuration
 * @param columnData - Data rows to export
 */
export function CSVExportButton<T>({
  title,
  buttonLabel = 'Export CSV',
  columnInfo,
  columnData,
}: {
  title: string
  buttonLabel?: string
  columnInfo: Column<T>[]
  columnData: T[]
}) {
  const [confirming, setConfirming] = useState(false)
  const [filteredCols, setFilteredCols] = useState<string[]>([])

  const exportable = columnInfo.filter(({ skipExport }) => !skipExport)

  const isDisabled = !columnInfo?.length || !columnData?.length

  const doExport = () => {
    const filtered = exportable.filter(
      ({ title }) => !filteredCols.includes(String(title)),
    )
    downloadCSV(title, filtered, columnData)
    setConfirming(false)
  }

  const onChangeChecked = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setFilteredCols((prev) => prev.filter((n) => n !== e.target.value))
    } else {
      setFilteredCols((prev) => [...prev, e.target.value])
    }
  }

  return (
    <>
      <Button disabled={isDisabled} onClick={() => setConfirming(true)}>
        {buttonLabel}
      </Button>
      <ConfirmDialog
        title={'Columns To Export'}
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={doExport}
      >
        <Box>
          {exportable.map(({ title }) => {
            const asString = String(title)
            return (
              <Box key={asString}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={asString}
                      checked={!filteredCols.includes(asString)}
                      onChange={onChangeChecked}
                      name={asString}
                    />
                  }
                  label={title}
                />
              </Box>
            )
          })}
        </Box>
      </ConfirmDialog>
    </>
  )
}

function downloadCSV(
  title: string,
  columnInfo: Column<unknown>[],
  columnData: unknown[],
) {
  let csvData: string[][] = []
  if (columnInfo && columnData) {
    csvData = generateCSVForTableData(columnInfo, columnData)
  } else {
    return
  }
  exportCsv(csvData, title)
}

function generateCSVForTableData<T>(
  columnInfo: Column<T>[],
  columnData: T[],
): string[][] {
  const headers = columnInfo.map(({ title }) => String(title))
  return columnData.reduce<string[][]>(
    (acc, colData, rowIndex) => {
      return [
        ...acc,
        columnInfo.map((colInfo, colIndex) => {
          if (colInfo.rowString) {
            return colInfo.rowString(colData)
          }
          const data = colInfo.rowContent(colData, { rowIndex, colIndex })
          return String(data)
        }),
      ]
    },
    [headers],
  )
}
